/* eslint-disable @typescript-eslint/no-explicit-any */
import { EventSourceMessage } from '@microsoft/fetch-event-source';
import { AgentExecutionResult, useMarriottAIPreviewStore } from '../../store';
import { useSearchFormStore } from '../../store/searchFormStore';
import { DATES, ROOMS_AND_GUESTS, USE_POINTS, SPECIAL_RATES } from '../../store/store.constants';
import { toolToLoadingMessage } from '../loadingMessageUtils';
import {
  syncSearchFormFromNLSResponse,
  syncSearchFormFromCalendarCta,
  findPropertyFromMessages,
  transformCalendarResponseToNLSStructure,
  buildAmpRequestFromCta,
} from '../ampRequestMapper';
import { formatUiMessage, markBestMatchProperties, FormattedUiData } from '../uiDataUtils';
import { uiContextManager } from '../uiContextManager';
import { SSEComponentCallbacks, SSERefs, SSEConstants } from './types';
import { CtaSearchCriteriaSnapshot } from '../../molecules/MDefaultTemplate/MDefaultTemplate.types';

/**
 * SSEEventManager handles all SSE event listeners for the chat application.
 * It encapsulates the event handling logic while keeping refs in the OChat component.
 *
 * Store actions are accessed directly from Zustand stores.
 * Only component-level callbacks (hooks/context-dependent) are passed via constructor.
 *
 * Usage:
 * ```
 * const manager = new SSEEventManager(componentCallbacks, refs, constants);
 * const messageHandler = manager.createMessageHandler(chatId);
 * // Pass messageHandler to tipSSEClient.connectStream()
 * ```
 */
export class SSEEventManager {
  private componentCallbacks: SSEComponentCallbacks;
  private refs: SSERefs;
  private constants: SSEConstants;

  constructor(componentCallbacks: SSEComponentCallbacks, refs: SSERefs, constants: SSEConstants) {
    this.componentCallbacks = componentCallbacks;
    this.refs = refs;
    this.constants = constants;
  }

  /**
   * Get store actions directly from Zustand stores
   */
  private getStoreActions() {
    const mainStore = useMarriottAIPreviewStore.getState();
    const searchFormStore = useSearchFormStore.getState();
    return {
      handleAPIResponse: mainStore.handleAPIResponse,
      enqueueLoadingMessage: mainStore.enqueueLoadingMessage,
      clearLoadingMessage: mainStore.clearLoadingMessage,
      setIsProcessing: mainStore.setIsProcessing,
      setLoading: mainStore.setLoading,
      setStatus: mainStore.setStatus,
      setSearchFormState: searchFormStore.setSearchFormState,
    };
  }

  /**
   * Get current store state
   */
  private getStoreState() {
    return useMarriottAIPreviewStore.getState();
  }

  /**
   * Capture current search criteria snapshot from the search form store.
   * This is used to attach to CTAs so they have stable URL construction data.
   */
  private captureSearchCriteriaSnapshot(): CtaSearchCriteriaSnapshot {
    const searchFormState = useSearchFormStore.getState();
    const dates = searchFormState[DATES];
    const rooms = searchFormState[ROOMS_AND_GUESTS];
    const usePoints = searchFormState[USE_POINTS];
    const specialRates = searchFormState[SPECIAL_RATES];

    return {
      checkInDate: dates?.fromDate?.format?.('YYYY-MM-DD') ?? undefined,
      checkOutDate: dates?.toDate?.format?.('YYYY-MM-DD') ?? undefined,
      flexibleDate: dates?.flexible === 'true' || (dates?.flexible as unknown) === true,
      numRooms: typeof rooms?.numRooms === 'string' ? parseInt(rooms.numRooms, 10) : rooms?.numRooms ?? 1,
      numAdultsPerRoom: rooms?.numAdultsPerRoom ?? 1,
      numChildrenPerRoom: rooms?.numChildrenPerRoom ?? 0,
      childrenAges: rooms?.childrenAges ?? [],
      useRewardsPoints: usePoints?.rewardsRedemption === true,
      clusterCode: specialRates?.clusterCode || undefined,
      corporateCode: specialRates?.corporateCode || undefined,
      groupCode: specialRates?.groupCode || undefined,
    };
  }

  /**
   * Attach search criteria snapshot to CTA in uiData if present.
   * This ensures each CTA has its own stable search criteria for URL construction.
   */
  private attachSearchCriteriaToUiData(uiData: any): any {
    if (!uiData || typeof uiData !== 'object') return uiData;

    // If uiData has a cta, attach the search criteria snapshot
    if (uiData.cta && typeof uiData.cta === 'object') {
      const snapshot = this.captureSearchCriteriaSnapshot();
      return {
        ...uiData,
        cta: {
          ...uiData.cta,
          searchCriteriaSnapshot: snapshot,
        },
      };
    }

    return uiData;
  }

  /**
   * Create a message handler function for @microsoft/fetch-event-source
   * This replaces the old addEventListener pattern with a single callback
   * that dispatches events by their event type.
   */
  createMessageHandler(chatId: string): (event: EventSourceMessage) => void {
    return (event: EventSourceMessage) => {
      const eventType = event.event;
      const eventData = event.data;

      // Report event ID to component for tracking (stored in ref, persisted on close)
      if (event.id) {
        this.componentCallbacks.onEventIdReceived?.(chatId, event.id);
      }

      switch (eventType) {
        case 'node_planning_started':
          this.handleNodePlanningStarted(chatId, eventData);
          break;
        case 'node_tool_progress':
          this.handleNodeToolProgress(chatId, eventData);
          break;
        case 'node_planning_completed':
          this.handleNodePlanningCompleted(chatId, eventData);
          break;
        case 'node_tool_executed':
          this.handleNodeToolExecuted(chatId, eventData);
          break;
        case 'node_failed':
          this.handleNodeFailed(chatId, eventData);
          break;
        case 'guardrail_blocked':
          this.handleGuardrailBlocked(chatId);
          break;
        case 'session_aborted':
          this.handleSessionAborted(chatId, eventData);
          break;
        case 'session_ended':
          this.handleSessionEnded(chatId);
          break;
        default:
          break;
      }
    };
  }

  /**
   * Handle SSE error events - closes stream (persists lastEventId) and resets state
   */
  handleError(chatId: string): void {
    this.componentCallbacks.onCloseStream?.(chatId);
    this.componentCallbacks.onResponseComplete?.(chatId);
  }

  /**
   * Handle node_tool_progress events - accumulates preview data from search results
   */
  private handleNodeToolProgress(chatId: string, rawData: string): void {
    const storeState = this.getStoreState();
    const targetChat = storeState.chatSessions.find((c: any) => c.id === chatId);
    const isChatProcessing = targetChat?.isProcessing ?? false;

    if (!isChatProcessing) {
      return;
    }

    try {
      const tipEvent = JSON.parse(rawData);
      const payload = tipEvent?.payload ?? {};
      const eventName = payload?.event_name;

      // Handle availability_calendar_search_response - single property availability search
      if (eventName === 'availability_calendar_search_response') {
        this.handleAvailabilityCalendarSearch(chatId, payload);
        return;
      }

      // Handle standard NLS hotel search
      const payloadRawData = payload?.rawData;
      const intent = String(payload?.intent ?? 'hotel_search');
      const metadata = {
        intent,
        toolsUsed: [payload?.tool],
        tipEventType: tipEvent?.event_type,
        eventName: payload?.event_name,
      };

      const edges = payloadRawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.edges ?? [];
      const itemCount = Array.isArray(edges) ? edges.length : 0;

      const isActiveChat = chatId === storeState.currentChatId;
      if (isActiveChat) {
        const { setSearchFormState } = this.getStoreActions();
        syncSearchFormFromNLSResponse(payloadRawData, setSearchFormState, this.constants.DATES, payload);
      }

      if (itemCount > 0) {
        // Extract ampRequest from NLS response for per-message search criteria
        const ampRequest = payloadRawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.ampRequest;

        this.refs.pendingPreview.current[chatId] = {
          viewData: {
            rawData: payloadRawData,
            query: payload?.query,
            isFlexDate: payload?.isFlexDate === true,
            ampRequest, // Store ampRequest snapshot with the message
            isSinglePropertyResponse: false, // Explicitly set to false for NLS responses
          },
          itemCount,
          intent,
          toolsUsed: metadata.toolsUsed,
        };
      }

      if (metadata.eventName === 'hotel_search_completed') {
        const { enqueueLoadingMessage } = this.getStoreActions();
        enqueueLoadingMessage('REVIEWING_OPTIONS', chatId);

        // Clear the search modified flag for this conversation - TIP has returned new search results,
        // so the backend has acknowledged the user's search modifications.
        // This ensures ui_context is not sent again until user modifies search again.
        uiContextManager.clearSearchModifiedFlag(chatId);
      }
    } catch {
      // ignore malformed node_tool_progress events
    }
  }

  /**
   * Handle availability_calendar_search_response events - single property availability search
   * Transforms the calendar response to NLS-like structure and stores it as pending preview
   */
  private handleAvailabilityCalendarSearch(chatId: string, payload: any): void {
    const storeState = this.getStoreState();
    const targetChat = storeState.chatSessions.find((c: any) => c.id === chatId);
    const messages = targetChat?.messages ?? [];

    const propertyId = payload?.property_id;
    const cta = payload?.cta;

    // First, check if there's NLS data in the current pendingPreview (same message response)
    // This handles the case where NLS and availability_calendar_search come in the same message
    let existingPropertyEdge = this.findPropertyFromPendingPreview(chatId, propertyId);

    // If not found in pendingPreview, search in committed messages
    if (!existingPropertyEdge) {
      existingPropertyEdge = findPropertyFromMessages(messages, propertyId);
    }

    // Transform calendar response to NLS-like structure
    const transformedRawData = transformCalendarResponseToNLSStructure(payload, existingPropertyEdge);

    // Build ampRequest from CTA for per-message search criteria
    const ampRequest = buildAmpRequestFromCta(cta);

    // Determine if this is a flexible date search based on userSpecifiedDate from CTA
    // userSpecifiedDate: true → user specified exact dates → isFlexDate: false
    // userSpecifiedDate: false → flexible dates → isFlexDate: true
    // userSpecifiedDate: undefined → default to specific dates (false) for calendar search
    const isFlexDate = cta?.userSpecifiedDate !== undefined ? !cta.userSpecifiedDate : false;

    // Sync search form if this is the active chat
    const isActiveChat = chatId === storeState.currentChatId;
    if (isActiveChat) {
      const { setSearchFormState } = this.getStoreActions();
      syncSearchFormFromCalendarCta(cta, setSearchFormState);
    }

    // Store as pending preview with single property flag
    this.refs.pendingPreview.current[chatId] = {
      viewData: {
        rawData: transformedRawData,
        isSinglePropertyResponse: true,
        ampRequest,
        isFlexDate,
      },
      itemCount: 1,
      intent: 'availability_calendar_search',
      toolsUsed: ['availability_calendar_search'],
    };

    // Enqueue loading message
    const { enqueueLoadingMessage } = this.getStoreActions();
    enqueueLoadingMessage('REVIEWING_OPTIONS', chatId);
  }

  /**
   * Find property data from the current pendingPreview (for same-message NLS + calendar search)
   */
  private findPropertyFromPendingPreview(chatId: string, propertyId: string): any | null {
    if (!propertyId) return null;

    const pendingPreview = this.refs.pendingPreview.current[chatId];
    if (!pendingPreview) return null;

    const edges =
      pendingPreview.viewData?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.edges;
    if (edges && Array.isArray(edges)) {
      const match = edges.find((e: any) => e.node?.property?.id === propertyId);
      if (match) return match;
    }
    return null;
  }

  /**
   * Handle node_planning_started events - triggers REFINING_RESULTS when is_reflection is true
   */
  private handleNodePlanningStarted(chatId: string, rawData: string): void {
    const storeState = this.getStoreState();
    const targetChat = storeState.chatSessions.find((c: any) => c.id === chatId);
    const isChatProcessing = targetChat?.isProcessing ?? false;

    if (!isChatProcessing) return;

    try {
      const tipEvent = JSON.parse(rawData);
      const isReflection = tipEvent?.payload?.is_reflection === true;
      if (isReflection) {
        const { enqueueLoadingMessage } = this.getStoreActions();
        enqueueLoadingMessage('REFINING_RESULTS', chatId);
      }
    } catch {
      // ignore malformed node_planning_started events
    }
  }

  /**
   * Handle node_planning_completed events - updates loading message based on tool.
   * Also activates the search input loader exclusively for nls_hotel_search.
   */
  private handleNodePlanningCompleted(chatId: string, rawData: string): void {
    const storeState = this.getStoreState();
    const targetChat = storeState.chatSessions.find((c: any) => c.id === chatId);
    const isChatProcessing = targetChat?.isProcessing ?? false;

    if (!isChatProcessing) return;

    try {
      const tipEvent = JSON.parse(rawData);
      const tool: string | undefined = tipEvent?.payload?.tool;
      const phase = toolToLoadingMessage(tool);
      if (phase !== null) {
        const { enqueueLoadingMessage, setLoading } = this.getStoreActions();
        enqueueLoadingMessage(phase, chatId);
        if (phase === 'HOTEL_SEARCH') {
          setLoading(true);
        }
      }
    } catch {
      // ignore malformed node_planning_completed events
    }
  }

  /**
   * Handle node_tool_executed events - processes format_response and creates API result
   */
  private handleNodeToolExecuted(chatId: string, rawData: string): void {
    const storeState = this.getStoreState();
    const targetChat = storeState.chatSessions.find((c: any) => c.id === chatId);
    const isChatProcessing = targetChat?.isProcessing ?? false;

    if (!isChatProcessing) {
      return;
    }

    const isActiveChat = chatId === storeState.currentChatId;

    try {
      const tipEvent = JSON.parse(rawData);
      const payload = tipEvent?.payload ?? {};
      const toolName = payload?.tool_name || payload?.tool;

      if (toolName !== 'format_response') return;

      const result = payload?.result ?? {};
      const rawUiData = result?.uiData as FormattedUiData | string | null | undefined;
      const uiType = typeof result?.uiType === 'string' ? result.uiType : null;

      // Attach search criteria snapshot to CTA if present
      const uiData = this.attachSearchCriteriaToUiData(rawUiData);

      this.refs.uiData.current[chatId] = uiData ?? null;
      this.refs.uiType.current[chatId] = uiType;

      const formattedUiMessage = formatUiMessage(uiData, result);

      this.refs.formattedUiData.current[chatId] = formattedUiMessage;
      this.refs.formattedResponse.current[chatId] = {
        uiData: uiData ?? null,
        uiType,
        formattedMessage: formattedUiMessage,
      };

      const pending = this.refs.pendingPreview.current[chatId];
      markBestMatchProperties(pending, uiData);

      const responseText = formattedUiMessage ?? (typeof result?.response === 'string' ? result.response : '');
      const lastEventId = this.refs.lastEventId?.current[chatId];
      const apiResult: AgentExecutionResult = {
        viewType: 'hotel-list',
        viewData: pending?.viewData,
        intent: pending?.intent as string,
        toolsUsed: pending?.toolsUsed ?? [],
        response: typeof responseText === 'string' ? responseText : '',
        chatId: chatId,
        uiData: uiData ?? null,
        uiType: uiType ?? undefined,
        metadata: {
          executionTime: 0,
        },
        lastEventId,
      };

      if (isActiveChat) {
        this.componentCallbacks.handleCloseHotelPanel();
      }

      const { handleAPIResponse } = this.getStoreActions();
      handleAPIResponse(apiResult);

      this.clearChatRefs(chatId);
      this.clearLoadingState(chatId);
      this.componentCallbacks.onResponseComplete?.(chatId);
    } catch {
      // ignore malformed node_tool_executed events
    }
  }

  /**
   * Clear chat-specific refs after processing
   */
  private clearChatRefs(chatId: string): void {
    this.refs.uiData.current[chatId] = null;
    this.refs.uiType.current[chatId] = null;
    this.refs.formattedUiData.current[chatId] = null;
    this.refs.formattedResponse.current[chatId] = null;
  }

  /**
   * Clear loading state after processing completes.
   */
  private clearLoadingState(chatId: string): void {
    const { clearLoadingMessage, setIsProcessing, setLoading, setStatus } = this.getStoreActions();
    clearLoadingMessage(chatId);
    setIsProcessing(false, chatId);
    setLoading(false);
    setStatus({ status: 'complete' }, chatId);
    setTimeout(() => setStatus({ status: 'idle' }, chatId), 500);
  }

  /**
   * Handle guardrail_blocked events - creates error response
   */
  private handleGuardrailBlocked(chatId: string): void {
    try {
      const apiResult: AgentExecutionResult = {
        intent: 'guardrail_blocked',
        toolsUsed: [],
        response: this.constants.GUARDRAIL_ERROR_MESSAGE,
        chatId: chatId,
        uiData: { content: this.constants.GUARDRAIL_ERROR_MESSAGE },
        uiType: 'default',
        metadata: {
          executionTime: 0,
        },
      };

      const { handleAPIResponse } = this.getStoreActions();
      handleAPIResponse(apiResult);
      this.clearLoadingState(chatId);
      this.componentCallbacks.onResponseComplete?.(chatId);
    } catch {
      // ignore malformed guardrail_blocked events
    }
  }

  /**
   * Handle session_aborted events - disables chat when guardrail limit is exceeded
   */
  private handleSessionAborted(chatId: string, rawData: string): void {
    try {
      const tipEvent = JSON.parse(rawData);
      const payload = tipEvent?.payload ?? {};
      const reason = payload?.reason;

      if (reason === 'guardrail_limit_exceeded') {
        const mainStore = useMarriottAIPreviewStore.getState();

        mainStore.setChatDisabled(chatId, true);
        mainStore.clearChatTipSession(chatId);

        mainStore.addMessageToChat(chatId, {
          id: Date.now().toString() + '-guardrail-limit',
          content: this.constants.GUARDRAIL_LIMIT_EXCEEDED_MESSAGE,
          isUser: false,
          timestamp: new Date(),
          toolsUsed: [],
          uiType: 'guardrail_limit_exceeded',
        });

        this.clearLoadingState(chatId);
        this.componentCallbacks.onCloseStream?.(chatId);
        this.componentCallbacks.onResponseComplete?.(chatId);
      }
    } catch {
      // ignore malformed session_aborted events
    }
  }

  /**
   * Handle session_ended events - closes stream (persists lastEventId) and resets state
   */
  private handleSessionEnded(chatId: string): void {
    this.componentCallbacks.onCloseStream?.(chatId);
    this.componentCallbacks.onResponseComplete?.(chatId);
  }

  /**
   * Handle node_failed events - detects authentication failures and notifies component
   * This is triggered when the token expires while SSE connection is open
   */
  private handleNodeFailed(chatId: string, rawData: string): void {
    try {
      const tipEvent = JSON.parse(rawData);
      const error = tipEvent?.payload?.error ?? '';

      if (error.includes('Authentication failed') || error.includes('bearer token')) {
        this.componentCallbacks.onAuthenticationFailed?.(chatId);
      }
    } catch {
      // ignore malformed node_failed events
    }
  }
}
